import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Suppress ResizeObserver loop warnings (harmless browser quirk during animations)
// This occurs when ResizeObserver callbacks trigger layout changes during transitions
// Also suppresses WebSocket connection errors from webpack-dev-server HMR
const suppressResizeObserverError = () => {
  // Check if ResizeObserver error should be suppressed
  const isResizeObserverError = (message) => {
    if (typeof message !== 'string') return false;
    const lowerMessage = message.toLowerCase();
    return lowerMessage.includes('resizeobserver') && (
      lowerMessage.includes('loop') ||
      lowerMessage.includes('undelivered notifications') ||
      lowerMessage.includes('limit exceeded')
    );
  };

  // Check if WebSocket error should be suppressed (development only)
  const isWebSocketError = (message) => {
    if (process.env.NODE_ENV !== 'development') return false;
    if (typeof message !== 'string') return false;
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('websocket connection to') ||
      lowerMessage.includes('ws://localhost') ||
      (lowerMessage.includes('failed to load resource') && lowerMessage.includes('ws'))
    );
  };

  // Suppress window.onerror
  const originalError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (isResizeObserverError(message) || isWebSocketError(message)) {
      return true; // Suppress
    }
    if (originalError) {
      return originalError.apply(this, arguments);
    }
    return false;
  };

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason) {
      const message = typeof event.reason === 'string' 
        ? event.reason 
        : (event.reason.message || String(event.reason));
      if (isResizeObserverError(message) || isWebSocketError(message)) {
        event.preventDefault();
      }
    }
  });

  // Suppress console errors and warnings
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  
  console.error = function(...args) {
    const firstArg = args[0];
    const message = typeof firstArg === 'string' 
      ? firstArg 
      : (firstArg?.message || String(firstArg));
    if (isResizeObserverError(message) || isWebSocketError(message)) {
      return; // Suppress
    }
    originalConsoleError.apply(console, args);
  };

  console.warn = function(...args) {
    const firstArg = args[0];
    const message = typeof firstArg === 'string' 
      ? firstArg 
      : (firstArg?.message || String(firstArg));
    if (isResizeObserverError(message)) {
      return; // Suppress
    }
    originalConsoleWarn.apply(console, args);
  };

  // Override ResizeObserver with improved error handling and performance
  // Note: This is a secondary override in case the one in index.html didn't catch it
  if (typeof window.ResizeObserver !== 'undefined' && !window.ResizeObserver._suppressed) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        let rafId;
        let timeoutId;
        let isProcessing = false;
        
        const safeCallback = (...args) => {
          // Prevent multiple simultaneous callbacks
          if (isProcessing) {
            return;
          }
          
          // Clear any pending callbacks
          if (rafId) {
            cancelAnimationFrame(rafId);
          }
          clearTimeout(timeoutId);
          
          timeoutId = setTimeout(() => {
            isProcessing = true;
            rafId = requestAnimationFrame(() => {
              try {
                callback(...args);
              } catch (e) {
                // Only suppress ResizeObserver-specific errors
                if (!isResizeObserverError(e.message || String(e))) {
                  throw e;
                }
              } finally {
                isProcessing = false;
                rafId = null;
              }
            });
          }, 0);
        };
        
        super(safeCallback);
      }
    };
    window.ResizeObserver._suppressed = true; // Mark as suppressed
  }
};

suppressResizeObserverError();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // <React.StrictMode>
  <App />
  // </React.StrictMode>
);

// Register service worker for PWA functionality
// Note: beforeinstallprompt event requires HTTPS (or localhost) and valid PWA setup
serviceWorkerRegistration.register({

    onSuccess: (registration) => {

        console.log(
            'Service Worker registered successfully.'
        );

    },

    onUpdate: (registration) => {

        console.log(
            'New version detected. Updating application...'
        );

        if (registration.waiting) {

            registration.waiting.postMessage({
                type: 'SKIP_WAITING'
            });

        }

    }

});


